import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import logo from "./images/techvanto-logo.png";

const LOGO_SRC = logo;

// ── Nav Data ──────────────────────────────────────────────────────────────────
const NAV_LINKS = [
  {
    label: "Services",
    dropdown: [
      { name: "Web Development", link: "/web-development" },
      { name: "Mobile App Development", link: "/mobile-app-development" },
      { name: "AI & ML Solutions", link: "/ai-ml-solutions" },
      { name: "Cloud DevOps", link: "/cloud" },
      { name: "UI/UX Design", link: "/ui-ux" },
      { name: "E-Commerce Solutions", link: "/ecommerce" },
    ],
  },
  {
    label: "Solutions",
     dropdown: [
      { name: "CRM Solution", link: "/crm-solutions" },
      { name: "ERP Solution", link: "/erp-solutions" },
      { name: "HRM Solution", link: "/hrm-solutions" },
      { name: "LMS Solution", link: "/lms-solutions" },
      { name: "POS Solution", link: "/pos-solutions" },
      { name: "SaaS Platform", link: "/saas-platform" },
    ],
  },
  {
    label: "Industries",
    dropdown: [
      { name: "Healthcare", link: "/healthcare-solution" },
      { name: "FinTech", link: "/fintech-solution" },
      { name: "EdTech", link: "/edtech-solution" },
      { name: "Retail & E-Commerce", link: "/retail-ecommerce-solution" },
      { name: "Real Estate", link: "realestate-solution" },
      { name: "Logistics", link: "/logistics-solution" },
    ],
  },
  { label: "Portfolio", dropdown: null, link: "/portfolio" },
  { label: "About", dropdown: null, link: "/#about" },
  { label: "Blog", dropdown: null, link: "/blogs" },
];

// ── Page Data ─────────────────────────────────────────────────────────────────

const SERVICES = [
  {
    icon: "🎓",
    title: "Custom LMS Development",
    desc: "Full-featured, white-label Learning Management Systems — course builder, SCORM/xAPI compliance, video delivery, quizzes, assignments, certificates, progress tracking, and instructor dashboards built from the ground up.",
    tags: ["SCORM", "xAPI", "LTI 1.3", "White-Label"],
    color: "#F97316",
  },
  {
    icon: "📱",
    title: "Mobile Learning App",
    desc: "Native iOS and Android learning apps with offline content sync, push notifications, gamified streaks, microlearning modules, and live session integration — learning that fits any learner's lifestyle.",
    tags: ["iOS & Android", "Offline Mode", "Microlearning", "Flutter"],
    color: "#10B981",
  },
  {
    icon: "🤖",
    title: "AI-Powered Personalised Learning",
    desc: "Adaptive learning engines that adjust content difficulty, sequence lessons, generate AI quizzes, provide instant feedback, and create personalised study plans based on each learner's performance data.",
    tags: ["Adaptive Learning", "GPT-4", "AI Quizzes", "Personalisation"],
    color: "#8B5CF6",
  },
  {
    icon: "📹",
    title: "Live & Virtual Classroom",
    desc: "Real-time virtual classrooms with HD video, interactive whiteboards, screen sharing, breakout rooms, live polls, hand-raise, attendance tracking, and session recording with auto-transcription.",
    tags: ["WebRTC", "Zoom SDK", "Whiteboard", "Breakout Rooms"],
    color: "#2563EB",
  },
  {
    icon: "🏆",
    title: "Gamification & Engagement Engine",
    desc: "Points, badges, leaderboards, XP levels, achievement streaks, social learning feeds, peer challenges, and reward marketplaces — transforming mandatory learning into genuinely engaging experiences.",
    tags: ["Gamification", "Leaderboards", "Badges", "Social Learning"],
    color: "#DC2626",
  },
  {
    icon: "📊",
    title: "Learning Analytics & Reporting",
    desc: "Real-time dashboards for learners, instructors, and admins — tracking completion rates, quiz performance, time-on-task, engagement heatmaps, cohort comparisons, and learning outcome ROI.",
    tags: ["Dashboards", "Completion Tracking", "Cohort Analysis", "BI"],
    color: "#0891B2",
  },
  {
    icon: "💰",
    title: "Course Marketplace & Monetisation",
    desc: "Sell courses individually, via subscriptions, or in bundles — with coupon codes, affiliate programs, multi-instructor revenue splits, Razorpay/Stripe integration, and a creator earnings dashboard.",
    tags: ["Stripe", "Razorpay", "Subscriptions", "Affiliate Program"],
    color: "#D97706",
  },
  {
    icon: "🔗",
    title: "EdTech Integrations & API Platform",
    desc: "SSO with Google/Microsoft, HRMS/SIS integrations, Zoom/Teams plugins, payment gateways, WhatsApp notifications, content authoring tool imports (Articulate, Adobe), and open REST/LTI APIs.",
    tags: ["SSO", "Articulate", "WhatsApp API", "REST API"],
    color: "#0D9488",
  },
];

const TECH_STACK = [
  {
    category: "Frontend",
    items: [
      { name: "React.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg", color: "#61DAFB" },
      { name: "Next.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nextjs/nextjs-original.svg", color: "#000000" },
      { name: "Flutter", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flutter/flutter-original.svg", color: "#02569B" },
      { name: "TypeScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg", color: "#3178C6" },
      { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original.svg", color: "#42B883" },
      { name: "Tailwind CSS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg", color: "#06B6D4" },
    ],
  },
  {
    category: "Backend & AI",
    items: [
      { name: "Node.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg", color: "#339933" },
      { name: "Python", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#3776AB" },
      { name: "Django", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/django/django-plain.svg", color: "#092E20" },
      { name: "TensorFlow", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tensorflow/tensorflow-original.svg", color: "#FF6F00" },
      { name: "GraphQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/graphql/graphql-plain.svg", color: "#E10098" },
      { name: "PyTorch", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/pytorch/pytorch-original.svg", color: "#EE4C2C" },
    ],
  },
  {
    category: "Video & Media",
    items: [
      { name: "AWS S3", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg", color: "#FF9900" },
      { name: "CloudFront CDN", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg", color: "#FF6B35" },
      { name: "FFmpeg", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/linux/linux-original.svg", color: "#007808" },
      { name: "WebRTC", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/chrome/chrome-original.svg", color: "#333333" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg", color: "#DC382D" },
      { name: "Firebase", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/firebase/firebase-plain.svg", color: "#FFCA28" },
    ],
  },
  {
    category: "Cloud & Standards",
    items: [
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg", color: "#FF9900" },
      { name: "GCP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/googlecloud/googlecloud-original.svg", color: "#4285F4" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg", color: "#2496ED" },
      { name: "Kubernetes", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/kubernetes/kubernetes-plain.svg", color: "#326CE5" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg", color: "#4169E1" },
      { name: "MongoDB", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mongodb/mongodb-original.svg", color: "#47A248" },
    ],
  },
];

const PROCESS = [
  { n: "01", icon: "🔍", title: "Learning Needs Analysis & Discovery", desc: "Stakeholder workshops with instructional designers, educators, and learners — mapping learning objectives, content structure, assessment strategies, platform requirements, and success metrics before any design begins.", color: "#F97316" },
  { n: "02", icon: "🎨", title: "UX Research & Learner-Centred Design", desc: "User research with real learners and instructors. Wireframes validated in usability sessions, then high-fidelity designs optimised for engagement, accessibility (WCAG 2.1), and low cognitive load across all devices.", color: "#10B981" },
  { n: "03", icon: "⚙️", title: "Agile Development with Instructional QA", desc: "2-week sprints with dedicated EdTech QA — learning flow testing, SCORM runtime validation, video delivery benchmarking, and accessibility audits alongside automated test suites, with weekly demos you can interact with.", color: "#8B5CF6" },
  { n: "04", icon: "🎬", title: "Video Infrastructure & Content Delivery", desc: "Adaptive HLS video streaming, DRM content protection, SCORM/xAPI runtime engine, H5P interactive content, multilingual subtitle support, and CDN-accelerated global delivery for sub-2s load times worldwide.", color: "#D97706" },
  { n: "05", icon: "🧪", title: "Pilot Testing, UAT & Accessibility Audit", desc: "Pilot with a real cohort of learners, WCAG 2.1 AA accessibility certification, cross-device and cross-browser QA on 20+ combinations, load testing at 5× peak, and a structured UAT phase before full rollout.", color: "#DC2626" },
  { n: "06", icon: "🚀", title: "Launch, Onboarding & Continuous Growth", desc: "Phased rollout with instructor onboarding workshops, admin training, 24/7 SLA support, monthly learning analytics reviews, A/B testing of engagement features, and continuous iteration driven by learner data.", color: "#0891B2" },
];

const CASE_STUDIES = [
  {
    title: "SkillForge — Corporate Learning Platform",
    industry: "Corporate Training",
    desc: "Built a white-label corporate LMS for a pan-India FMCG company with 14,000 employees — AI-adaptive assessments, HRMS integration with SAP, gamified completion streaks, and an offline mobile app. Completion rates jumped from 28% to 91% in the first quarter.",
    img: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop&auto=format",
    tags: ["React", "Node.js", "SCORM", "SAP Integration"],
    metrics: [{ val: "91%", lbl: "Completion Rate" }, { val: "14K+", lbl: "Active Learners" }, { val: "3.2×", lbl: "Training ROI" }],
    color: "#F97316",
  },
  {
    title: "EduVerse — K-12 Learning App",
    industry: "K-12 Education",
    desc: "A gamified adaptive learning app for 80,000 school students across 200 schools — personalised study plans, AI doubt-solving chatbot, live classes, parent progress dashboards, and curriculum-mapped assessments aligned to CBSE and ICSE boards.",
    img: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=600&h=400&fit=crop&auto=format",
    tags: ["Flutter", "GPT-4", "WebRTC", "Firebase"],
    metrics: [{ val: "80K+", lbl: "Students" }, { val: "4.8★", lbl: "Play Store" }, { val: "200+", lbl: "Schools" }],
    color: "#10B981",
  },
  {
    title: "LearnLaunch — Course Marketplace",
    industry: "Online Education",
    desc: "A Udemy-style course marketplace for independent educators — multi-instructor payouts via Stripe Connect, DRM-protected video, AI course recommendations, affiliate tracking, and a mobile app with 120K+ downloads in 6 months.",
    img: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=600&h=400&fit=crop&auto=format",
    tags: ["Next.js", "Stripe Connect", "Mux Video", "GPT-4"],
    metrics: [{ val: "120K+", lbl: "App Downloads" }, { val: "₹3.1Cr", lbl: "Creator Payouts" }, { val: "4.9★", lbl: "App Store" }],
    color: "#8B5CF6",
  },
];

const WHY_US = [
  { icon: "🎓", title: "Deep EdTech Domain Expertise", desc: "Our team includes instructional designers, ex-ed-startup engineers, and learning scientists who understand pedagogy, SCORM standards, and learner psychology — not just software architecture.", color: "#F97316" },
  { icon: "🤖", title: "AI That Actually Improves Learning Outcomes", desc: "Our adaptive learning models, AI quiz generators, and personalised study plan engines are validated against real learner cohorts — not demo datasets — with measurable impact on completion and retention rates.", color: "#10B981" },
  { icon: "📱", title: "Mobile-First, Offline-Ready", desc: "Every EdTech product we build is designed for India's connectivity reality — offline content sync, low-bandwidth video streaming, and a sub-3MB app install that works on a ₹8,000 Android device.", color: "#8B5CF6" },
  { icon: "🔗", title: "Full Standards Compliance", desc: "SCORM 1.2, SCORM 2004, xAPI (Tin Can), AICC, and LTI 1.3 — we implement every e-learning standard correctly, so your content works across any LMS and your data flows into any LRS.", color: "#2563EB" },
  { icon: "⚡", title: "Video Delivery at Scale", desc: "Adaptive HLS streaming, DRM protection (Widevine + FairPlay), CloudFront CDN, and concurrent session handling — tested for 50,000+ simultaneous learners without buffering or degradation.", color: "#D97706" },
  { icon: "📊", title: "Measurable Learning ROI", desc: "We instrument every platform with learning analytics that go beyond completion rates — skill gap tracking, knowledge retention curves, and ROI dashboards that prove the business value of your learning programme.", color: "#0891B2" },
];

const FAQS = [
  {
    q: "Can you build a fully custom LMS or only configure existing platforms like Moodle or Open edX?",
    a: "Both — and we'll recommend the right approach after a free discovery session. For organisations needing specific workflows, custom branding, deep integrations, or unique learning mechanics, we build fully custom LMS platforms from scratch using React, Node.js, and a modern cloud stack. For tighter budgets or standard requirements, we heavily customise open-source platforms like Moodle, Open edX, or WordPress/LearnDash. The custom route gives you complete ownership and flexibility; the platform route gets you to market faster and cheaper.",
  },
  {
    q: "Does your LMS support SCORM, xAPI, and LTI standards?",
    a: "Yes — fully and correctly. We implement SCORM 1.2, SCORM 2004 (all four editions), xAPI (Tin Can), AICC, and LTI 1.3 in every LMS we build. This means you can import existing content packages from Articulate Storyline, Adobe Captivate, iSpring, and Lectora, and tracking data will flow accurately. For xAPI, we can connect to any Learning Record Store (LRS) including SCORM Cloud, learning Locker, and Watershed. LTI 1.3 allows your LMS to launch and receive grades from tools like Turnitin, Kaltura, and Google Workspace.",
  },
  {
    q: "How do you handle video streaming for thousands of concurrent learners?",
    a: "We use adaptive HLS (HTTP Live Streaming) bitrate encoding via Mux or AWS MediaConvert — automatically delivering the right video quality for each learner's connection speed, from 240p on 2G to 1080p on broadband. Content is served via CloudFront CDN with edge nodes across India and globally, giving sub-2s load times anywhere. Videos are protected with multi-DRM (Google Widevine for Android/web, Apple FairPlay for iOS) to prevent downloads. We've handled 50,000+ concurrent video sessions without degradation.",
  },
  {
    q: "Can you integrate the LMS with our existing HRMS, SIS, or SSO system?",
    a: "Absolutely. We've integrated EdTech platforms with SAP SuccessFactors, Workday, BambooHR, PeopleSoft, and custom HRMS systems for automated user provisioning, course assignment, and completion sync. For student information systems, we connect to Fedena, Classter, and custom SIS databases. SSO is implemented via SAML 2.0, OAuth 2.0, or OpenID Connect — supporting Google Workspace, Microsoft Azure AD, Okta, and any SAML-compliant identity provider. We also build WhatsApp and SMS notification pipelines for learner engagement.",
  },
  {
    q: "How do you implement gamification without making it feel gimmicky?",
    a: "We design gamification around genuine learning behaviours rather than arbitrary actions. Points are awarded for quality of engagement — time spent on challenging content, quiz improvement, helping peers — not just clicks. Badges are earned for meaningful milestones. Leaderboards are opt-in and peer-group scoped, not global (which causes disengagement in mixed-level cohorts). Every gamification element goes through user research with real learners from your target audience, and we A/B test engagement mechanics post-launch using learning analytics.",
  },
  {
    q: "What post-launch support and platform evolution do you offer?",
    a: "All EdTech projects come with a 3-month warranty covering bug fixes at no charge. Beyond that, our support retainers include 24/7 uptime monitoring, monthly learning analytics reports, content platform updates (new question types, content formats), security patches, and quarterly feature sprints driven by learner feedback. Most clients retain us as a long-term technology partner because EdTech platforms evolve continuously — new cohorts, new content types, new integrations, and new engagement mechanics are the norm, not the exception.",
  },
];

const STATS = [
  { val: "130+", lbl: "EdTech Platforms Built", icon: "🎓" },
  { val: "5M+", lbl: "Learners Served", icon: "👩‍🎓" },
  { val: "91%", lbl: "Avg Completion Rate", icon: "📈" },
  { val: "SCORM", lbl: "& xAPI Certified", icon: "✅" },
];

// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);

  const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.93)",
      backdropFilter: "blur(20px)",
      borderBottom: scrolled ? "1px solid #FED7AA" : "1px solid transparent",
      boxShadow: scrolled ? "0 2px 28px rgba(249,115,22,.08)" : "none",
      transition: "all .3s ease",
    }}>
      {/* Top bar */}
      <div style={{
        background: "linear-gradient(90deg,#7C2D12,#F97316,#10B981)",
        color: "#fff", fontSize: 12, padding: "6px 40px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <span>📞 +91 96462 06032</span>
          <span>✉️ info@techvanto.com</span>
        </div>
        <span className="topbar-right" style={{ opacity: .9 }}>🌏 Trusted by 1500+ clients across 20+ countries</span>
      </div>

      {/* Main bar */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 40px", height: 68, maxWidth: 1280, margin: "0 auto",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center", flexShrink: 0, textDecoration: "none" }}>
          <img src={LOGO_SRC} alt="Techvanto" style={{ height: 150, width: "auto", objectFit: "contain", display: "block" }} />
        </a>

        {/* Desktop links */}
        <div className="nav-links" style={{ alignItems: "center", gap: 4 }}>
          {NAV_LINKS.map(link => (
            <div key={link.label} style={{ position: "relative" }}
              onMouseEnter={() => link.dropdown && setActiveDropdown(link.label)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              {link.link ? (
                <a href={link.link} style={{
                  background: currentPath === link.link ? "#FFF7ED" : "none",
                  border: currentPath === link.link ? "1px solid #FED7AA" : "1px solid transparent",
                  cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                  color: currentPath === link.link ? "#F97316" : "#374151",
                  display: "flex", alignItems: "center", gap: 4,
                  fontFamily: "'Sora',sans-serif", borderRadius: 8, textDecoration: "none",
                  transition: "all .2s",
                }}>{link.label}</a>
              ) : (
                <button style={{
                  background: link.label === "Industries" ? "#FFF7ED" : "none",
                  border: link.label === "Industries" ? "1px solid #FED7AA" : "1px solid transparent",
                  cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                  color: link.label === "Industries" ? "#F97316" : activeDropdown === link.label ? "#F97316" : "#374151",
                  display: "flex", alignItems: "center", gap: 4, transition: "all .2s",
                  fontFamily: "'Sora',sans-serif", borderRadius: 8,
                }}>
                  {link.label}
                  {link.dropdown && <span style={{ fontSize: 10, transition: "transform .2s", transform: activeDropdown === link.label ? "rotate(180deg)" : "none" }}>▾</span>}
                </button>
              )}
              {link.dropdown && activeDropdown === link.label && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, zIndex: 200,
                  background: "#fff", border: "1px solid #FED7AA", borderRadius: 16,
                  boxShadow: "0 20px 60px rgba(249,115,22,.1)", minWidth: 230,
                  padding: "8px 0", animation: "fadeIn .2s ease",
                }}>
                  {link.dropdown.map(item => (
                    <a key={item.name} href={item.link} style={{
                      display: "block", padding: "10px 20px", fontSize: 13.5,
                      color: item.name === "EdTech" ? "#F97316" : "#374151",
                      background: item.name === "EdTech" ? "#FFF7ED" : "none",
                      fontWeight: item.name === "EdTech" ? 700 : 500,
                      textDecoration: "none", transition: "all .15s",
                      fontFamily: "'Sora',sans-serif",
                    }}
                      onMouseEnter={e => { e.target.style.background = "#FFF7ED"; e.target.style.color = "#F97316"; }}
                      onMouseLeave={e => {
                        e.target.style.background = item.name === "EdTech" ? "#FFF7ED" : "none";
                        e.target.style.color = item.name === "EdTech" ? "#F97316" : "#374151";
                      }}
                    >{item.name}</a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <a href="#contact" className="nav-cta" style={{
            background: "linear-gradient(135deg,#C2410C,#F97316)", color: "#fff",
            padding: "10px 22px", borderRadius: 10, fontSize: 13.5, fontWeight: 700,
            textDecoration: "none", boxShadow: "0 4px 16px rgba(249,115,22,.3)",
            transition: "transform .2s, box-shadow .2s", fontFamily: "'Sora',sans-serif",
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(249,115,22,.42)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(249,115,22,.3)"; }}
          >Get Free Consultation</a>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="mob-menu-btn" style={{
            background: mobileOpen ? "#FFF7ED" : "none",
            border: "1px solid #FED7AA", borderRadius: 8,
            padding: "8px 12px", cursor: "pointer", fontSize: 18, color: "#374151",
            transition: "all .2s",
          }}>{mobileOpen ? "✕" : "☰"}</button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div style={{
          background: "#fff", borderTop: "1px solid #FED7AA",
          maxHeight: "calc(100vh - 120px)", overflowY: "auto",
          boxShadow: "0 16px 48px rgba(0,0,0,.1)",
        }}>
          <div style={{ padding: "16px 24px 12px", borderBottom: "1px solid #FFF7ED" }}>
            <img src={LOGO_SRC} alt="Techvanto" style={{ height: 40, width: "auto", objectFit: "contain" }} />
          </div>
          <div style={{ padding: "8px 0" }}>
            {NAV_LINKS.map(link => (
              <div key={link.label}>
                {link.dropdown ? (
                  <>
                    <button onClick={() => setMobileDropdown(mobileDropdown === link.label ? null : link.label)} style={{
                      width: "100%", textAlign: "left", padding: "13px 24px",
                      background: mobileDropdown === link.label ? "#FFF7ED" : "none",
                      border: "none", borderBottom: "1px solid #FFF7ED",
                      color: mobileDropdown === link.label ? "#F97316" : "#374151",
                      fontWeight: 600, fontSize: 15, fontFamily: "'Sora',sans-serif",
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}>
                      {link.label}
                      <span style={{ fontSize: 11, color: "#9CA3AF", transition: "transform .2s", transform: mobileDropdown === link.label ? "rotate(180deg)" : "none" }}>▾</span>
                    </button>
                    {mobileDropdown === link.label && (
                      <div style={{ background: "#FFFBF5", borderBottom: "1px solid #FFF7ED" }}>
                        {link.dropdown.map(item => (
                          <a key={item.name} href={item.link} onClick={() => setMobileOpen(false)} style={{
                            display: "block", padding: "11px 24px 11px 40px", fontSize: 14,
                            color: item.name === "EdTech" ? "#F97316" : "#4B5563",
                            background: item.name === "EdTech" ? "#FFF7ED" : "none",
                            fontWeight: item.name === "EdTech" ? 700 : 500,
                            textDecoration: "none", borderBottom: "1px solid #FFF7ED",
                            fontFamily: "'Sora',sans-serif",
                          }}>→ {item.name}</a>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <a href={link.link || "#"} onClick={() => setMobileOpen(false)} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "13px 24px",
                    color: currentPath === link.link ? "#F97316" : "#374151",
                    background: currentPath === link.link ? "#FFF7ED" : "none",
                    fontWeight: 600, fontSize: 15, textDecoration: "none",
                    borderBottom: "1px solid #FFF7ED", fontFamily: "'Sora',sans-serif",
                  }}>
                    {link.label}
                    <span style={{ fontSize: 11, color: "#9CA3AF" }}>›</span>
                  </a>
                )}
              </div>
            ))}
          </div>
          <div style={{ padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
            <a href="#contact" onClick={() => setMobileOpen(false)} style={{
              background: "linear-gradient(135deg,#C2410C,#F97316)", color: "#fff",
              padding: "14px", borderRadius: 10, textAlign: "center",
              fontWeight: 700, fontSize: 15, textDecoration: "none",
              fontFamily: "'Sora',sans-serif", boxShadow: "0 6px 20px rgba(249,115,22,.3)",
            }}>🎓 Get Free Consultation</a>
            <a href="tel:+919646206032" style={{
              background: "#FFF7ED", color: "#7C2D12",
              padding: "14px", borderRadius: 10, textAlign: "center",
              fontWeight: 700, fontSize: 15, textDecoration: "none",
              fontFamily: "'Sora',sans-serif", border: "1px solid #FED7AA",
            }}>📞 Call: +91 96462 06032</a>
          </div>
        </div>
      )}
    </nav>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  const [counter, setCounter] = useState(0);
  const metrics = ["SCORM & xAPI Compliant", "5M+ Learners Served", "91% Avg Completion Rate", "130+ EdTech Platforms Built"];

  useEffect(() => {
    const t = setInterval(() => setCounter(c => (c + 1) % metrics.length), 2600);
    return () => clearInterval(t);
  }, []);

  return (
    <section style={{
      paddingTop: 148, paddingBottom: 100,
      background: "linear-gradient(155deg,#FFF7ED 0%,#ECFDF5 35%,#F5F3FF 65%,#F8FAFC 100%)",
      position: "relative", overflow: "hidden",
    }}>
      {/* Subtle grid */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: "linear-gradient(rgba(249,115,22,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(249,115,22,.05) 1px,transparent 1px)",
        backgroundSize: "48px 48px",
      }} />

      {/* Curved blobs */}
      <div style={{ position: "absolute", top: -180, right: -120, width: 700, height: 700, borderRadius: "61% 39% 70% 30%/30% 50% 50% 70%", background: "radial-gradient(circle,rgba(249,115,22,.1),rgba(16,185,129,.05) 50%,transparent 75%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -120, left: -80, width: 560, height: 560, borderRadius: "39% 61% 30% 70%/70% 30% 70% 30%", background: "radial-gradient(circle,rgba(139,92,246,.08),transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: "30%", right: "30%", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle,rgba(16,185,129,.06),transparent 70%)", pointerEvents: "none" }} />

      {/* Decorative learning icons */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {[
          { x: "6%", y: "18%", sym: "📚" },
          { x: "91%", y: "14%", sym: "🎓" },
          { x: "14%", y: "78%", sym: "✏️" },
          { x: "88%", y: "72%", sym: "🏆" },
          { x: "52%", y: "90%", sym: "💡" },
        ].map((item, i) => (
          <div key={i} style={{
            position: "absolute", left: item.x, top: item.y,
            fontSize: 20,
            opacity: 0.08 + i * 0.02,
            animation: `etPulse ${3 + i}s ease-in-out infinite ${i * 0.6}s`,
          }}>{item.sym}</div>
        ))}
      </div>

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 40px", position: "relative" }}>
        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32, fontSize: 13, color: "#9CA3AF" }}>
          <a href="/" style={{ color: "#F97316", textDecoration: "none", fontWeight: 600, fontFamily: "'Sora',sans-serif" }}>Home</a>
          <span>›</span>
          <span style={{ fontFamily: "'Sora',sans-serif" }}>Industries</span>
          <span>›</span>
          <span style={{ color: "#0F172A", fontWeight: 600, fontFamily: "'Sora',sans-serif" }}>EdTech</span>
        </div>

        <div className="et-hero-grid">
          {/* Left */}
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 99, padding: "6px 18px", marginBottom: 28 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#F97316", display: "inline-block", animation: "etPulse 2s infinite" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#7C2D12", fontFamily: "'Sora',sans-serif" }}>🎓 India's Trusted EdTech Technology Partner</span>
            </div>

            <h1 style={{
              fontFamily: "'Sora',sans-serif",
              fontSize: "clamp(36px,4.8vw,64px)",
              fontWeight: 900, color: "#1A1A2E",
              lineHeight: 1.1, marginBottom: 20,
            }}>
              EdTech Software<br />
              <span style={{ background: "linear-gradient(135deg,#F97316,#10B981,#8B5CF6)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Built to Inspire,
              </span><br />
              Engineered to Scale.
            </h1>

            <p style={{ fontSize: 17, color: "#4B5563", lineHeight: 1.85, maxWidth: 570, marginBottom: 36, fontFamily: "'Sora',sans-serif", fontWeight: 400 }}>
              We build SCORM-compliant LMS platforms, AI-powered adaptive learning apps, live virtual classrooms, gamified learning experiences, and course marketplaces — engineered for the engagement demands and scale of modern education.
            </p>

            {/* Rotating metric pill */}
            <div style={{ background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 12, padding: "13px 20px", marginBottom: 36, display: "inline-flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 18 }}>✨</span>
              <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14.5, color: "#7C2D12" }}>{metrics[counter]}</span>
            </div>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 48 }}>
              <a href="#contact" style={{
                background: "linear-gradient(135deg,#C2410C,#F97316)", color: "#fff",
                padding: "15px 32px", borderRadius: 12, fontWeight: 700, fontSize: 15,
                textDecoration: "none", boxShadow: "0 8px 28px rgba(249,115,22,.35)",
                transition: "transform .22s, box-shadow .22s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 36px rgba(249,115,22,.48)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(249,115,22,.35)"; }}
              >🎓 Start EdTech Project</a>
              <a href="#case-studies" style={{
                background: "#fff", color: "#1A1A2E",
                padding: "15px 30px", borderRadius: 12, fontWeight: 700, fontSize: 15,
                textDecoration: "none", border: "2px solid #FED7AA",
                transition: "all .22s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "#FFF7ED"; e.currentTarget.style.borderColor = "#FB923C"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#FED7AA"; }}
              >📂 View Case Studies</a>
            </div>

            {/* Trust signal row */}
            <div style={{ display: "flex", gap: 32, flexWrap: "wrap", alignItems: "center" }}>
              {[["130+", "EdTech Platforms"], ["5M+", "Learners Served"], ["91%", "Completion Rate"], ["SCORM", "& xAPI Ready"]].map(([v, l]) => (
                <div key={l}>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 22, color: "#F97316" }}>{v}</div>
                  <div style={{ fontSize: 11.5, color: "#9CA3AF", fontWeight: 600, marginTop: 2, fontFamily: "'Sora',sans-serif" }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Visual panel */}
          <div style={{ position: "relative" }}>
            {/* Main image card */}
            <div style={{
              borderRadius: 28, overflow: "hidden",
              boxShadow: "0 40px 100px rgba(249,115,22,.14), 0 0 0 1px rgba(249,115,22,.08)",
              background: "#fff", position: "relative",
            }}>
              {/* Dashboard chrome bar */}
              <div style={{ background: "#FFFBF5", borderBottom: "1px solid #FED7AA", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", gap: 6 }}>
                  {["#EF4444", "#F59E0B", "#10B981"].map(c => <div key={c} style={{ width: 11, height: 11, borderRadius: "50%", background: c }} />)}
                </div>
                <div style={{ flex: 1, background: "#FFF7ED", borderRadius: 8, padding: "5px 14px", fontSize: 12, color: "#F97316", fontWeight: 600, margin: "0 12px", fontFamily: "'Sora',sans-serif" }}>
                  🔒 skillforge-lms.techvanto.com
                </div>
                <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 600, fontFamily: "'Sora',sans-serif", background: "#FFF7ED", padding: "4px 10px", borderRadius: 99, border: "1px solid #FED7AA" }}>LIVE</div>
              </div>
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=640&h=380&fit=crop&auto=format"
                alt="EdTech Platform"
                style={{ width: "100%", height: 340, objectFit: "cover", display: "block" }}
              />
              {/* Overlay gradient */}
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 100, background: "linear-gradient(to top,rgba(26,26,46,.3),transparent)" }} />
            </div>

            {/* Floating badge — SCORM */}
            <div style={{ position: "absolute", top: -18, left: -22, background: "#fff", borderRadius: 18, padding: "14px 18px", boxShadow: "0 12px 40px rgba(249,115,22,.18)", border: "1.5px solid #FED7AA", animation: "float 3.2s ease-in-out infinite", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: 12, background: "linear-gradient(135deg,#F97316,#10B981)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>✅</div>
                <div>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 14, color: "#1A1A2E" }}>SCORM</div>
                  <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 500, fontFamily: "'Sora',sans-serif" }}>& xAPI Compliant</div>
                </div>
              </div>
            </div>

            {/* Floating badge — Completion */}
            <div style={{ position: "absolute", bottom: 20, right: -22, background: "linear-gradient(135deg,#C2410C,#F97316)", borderRadius: 18, padding: "14px 20px", boxShadow: "0 12px 36px rgba(249,115,22,.4)", animation: "float 3.8s ease-in-out infinite .6s", zIndex: 10, textAlign: "center" }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 22, color: "#fff" }}>91%</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.8)", fontWeight: 600, fontFamily: "'Sora',sans-serif" }}>Completion Rate 📈</div>
            </div>

            {/* Floating badge — AI */}
            <div style={{ position: "absolute", bottom: -18, left: 16, background: "#fff", borderRadius: 16, padding: "12px 16px", boxShadow: "0 8px 28px rgba(0,0,0,.09)", border: "1.5px solid #D1FAE5", animation: "float 4.2s ease-in-out infinite 1.2s", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <span style={{ fontSize: 20 }}>🤖</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#0F172A", fontFamily: "'Sora',sans-serif" }}>AI Adaptive Learning</div>
                  <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "'Sora',sans-serif" }}>Personalised study plans</div>
                </div>
              </div>
            </div>

            {/* Floating badge — Learners */}
            <div style={{ position: "absolute", top: 56, right: -20, background: "#fff", borderRadius: 14, padding: "11px 15px", boxShadow: "0 6px 22px rgba(139,92,246,.12)", border: "1.5px solid #EDE9FE", animation: "float 4.6s ease-in-out infinite 1.8s", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 18 }}>👩‍🎓</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 12, color: "#5B21B6", fontFamily: "'Sora',sans-serif" }}>5M+ Learners</div>
                  <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "'Sora',sans-serif" }}>Across India & Globe</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Stats Bar ─────────────────────────────────────────────────────────────────
function StatsBar() {
  return (
    <section style={{ background: "#1A1A2E", padding: "52px 40px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }} className="et-stats-grid">
        {STATS.map(s => (
          <div key={s.lbl} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 30, marginBottom: 8 }}>{s.icon}</div>
            <div style={{
              fontFamily: "'Sora',sans-serif",
              fontSize: "clamp(28px,3vw,44px)", fontWeight: 900, color: "#fff",
              lineHeight: 1,
            }}>{s.val}</div>
            <div style={{ color: "#FB923C", fontSize: 13, marginTop: 8, fontWeight: 500, fontFamily: "'Sora',sans-serif" }}>{s.lbl}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Services Grid ─────────────────────────────────────────────────────────────
function ServicesGrid() {
  const [hov, setHov] = useState(null);
  return (
    <section id="services" style={{ padding: "100px 40px", background: "#fff" }} className="et-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#F97316", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>What We Build</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#1A1A2E", marginTop: 10, marginBottom: 16 }}>EdTech Software Solutions</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 580, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>
            Learner-centred education technology built for engagement, outcomes, and scale — from a 50-student bootcamp to a 5-million-user learning platform.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {SERVICES.map((s, i) => (
            <div key={s.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 24, padding: "34px 30px",
                border: `2px solid ${hov === i ? s.color : "#FFF7ED"}`,
                boxShadow: hov === i ? `0 28px 72px ${s.color}1E` : "0 2px 16px rgba(0,0,0,.04)",
                transition: "all .32s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-8px)" : "none",
                position: "relative", overflow: "hidden", cursor: "pointer",
              }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${s.color},${s.color}55)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .32s" }} />
              <div style={{ width: 58, height: 58, borderRadius: 16, background: `${s.color}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 22, border: `1.5px solid ${s.color}20` }}>
                {s.icon}
              </div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 800, color: "#1A1A2E", marginBottom: 10 }}>{s.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.8, marginBottom: 20, fontFamily: "'Sora',sans-serif" }}>{s.desc}</p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18 }}>
                {s.tags.map(t => (
                  <span key={t} style={{ background: `${s.color}0E`, color: s.color, fontSize: 11, fontWeight: 700, padding: "4px 11px", borderRadius: 99, border: `1px solid ${s.color}22`, fontFamily: "'Sora',sans-serif" }}>{t}</span>
                ))}
              </div>
              <div style={{ color: s.color, fontWeight: 700, fontSize: 13, opacity: hov === i ? 1 : 0, transition: "opacity .22s", fontFamily: "'Sora',sans-serif" }}>Learn More →</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Why Choose Us ─────────────────────────────────────────────────────────────
function WhyUs() {
  const [hov, setHov] = useState(null);
  return (
    <section style={{ padding: "100px 40px", background: "#FFFBF5" }} className="et-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#F97316", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>Why Techvanto</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#1A1A2E", marginTop: 10, marginBottom: 16 }}>Pedagogical Depth Meets Engineering Excellence</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 540, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>What separates a generic IT company from a true EdTech technology partner.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: 24 }}>
          {WHY_US.map((p, i) => (
            <div key={p.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 24, padding: "32px 28px",
                border: `2px solid ${hov === i ? p.color : "#FED7AA"}`,
                boxShadow: hov === i ? `0 20px 56px ${p.color}16` : "0 2px 12px rgba(0,0,0,.04)",
                transition: "all .3s", transform: hov === i ? "translateY(-4px)" : "none",
                display: "flex", gap: 20,
              }}>
              <div style={{ width: 54, height: 54, borderRadius: 16, background: `${p.color}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0, border: `1.5px solid ${p.color}20` }}>
                {p.icon}
              </div>
              <div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: "#1A1A2E", marginBottom: 8 }}>{p.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.75, fontFamily: "'Sora',sans-serif" }}>{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Tech Stack ────────────────────────────────────────────────────────────────
function TechStack() {
  const [active, setActive] = useState(0);
  const [hov, setHov] = useState(null);
  const cat = TECH_STACK[active];

  return (
    <section style={{ padding: "100px 40px", background: "#fff" }} className="et-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#10B981", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>Technology</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#1A1A2E", marginTop: 10, marginBottom: 16 }}>EdTech Technology Stack</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>The frameworks, tools, and platforms powering our education technology solutions — chosen for learner experience and reliability.</p>
        </div>

        <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginBottom: 44 }}>
          {TECH_STACK.map((t, i) => (
            <button key={t.category} onClick={() => { setActive(i); setHov(null); }} style={{
              padding: "10px 22px", borderRadius: 99, fontWeight: 700, fontSize: 13.5,
              cursor: "pointer", border: "2px solid", transition: "all .25s",
              fontFamily: "'Sora',sans-serif",
              background: active === i ? "linear-gradient(135deg,#C2410C,#F97316)" : "#fff",
              borderColor: active === i ? "#F97316" : "#FED7AA",
              color: active === i ? "#fff" : "#6B7280",
              boxShadow: active === i ? "0 6px 20px rgba(249,115,22,.28)" : "none",
            }}>{t.category}</button>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 18 }}>
          {cat.items.map((tech, i) => (
            <div key={tech.name}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
                padding: "32px 20px 24px", borderRadius: 22,
                border: `2px solid ${hov === i ? tech.color : "#FFF7ED"}`,
                background: hov === i ? `${tech.color}07` : "#FFFBF5",
                boxShadow: hov === i ? `0 14px 42px ${tech.color}22` : "0 1px 6px rgba(0,0,0,.04)",
                transition: "all .3s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-7px) scale(1.03)" : "none",
                cursor: "default", position: "relative", overflow: "hidden",
              }}>
              <div style={{ width: 72, height: 72, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 18, background: hov === i ? `${tech.color}14` : "#fff", border: `1px solid ${hov === i ? tech.color + "28" : "#FED7AA"}`, boxShadow: hov === i ? `0 4px 18px ${tech.color}20` : "0 1px 4px rgba(0,0,0,.06)", transition: "all .3s" }}>
                <img src={tech.logo} alt={tech.name} style={{ width: 44, height: 44, objectFit: "contain", transition: "transform .3s", transform: hov === i ? "scale(1.12)" : "scale(1)" }}
                  onError={e => { e.target.style.display = "none"; e.target.parentNode.innerHTML = `<span style="font-size:22px;font-weight:900;color:${tech.color}">${tech.name[0]}</span>`; }}
                />
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: hov === i ? tech.color : "#1E293B", fontFamily: "'Sora',sans-serif", transition: "color .25s" }}>{tech.name}</div>
                {hov === i && <div style={{ fontSize: 10, color: tech.color, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", marginTop: 3, fontFamily: "'Sora',sans-serif" }}>Expert Level</div>}
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, borderRadius: "0 0 20px 20px", background: `linear-gradient(90deg,${tech.color},${tech.color}60)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .32s" }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Process ───────────────────────────────────────────────────────────────────
function ProcessSection() {
  return (
    <section style={{ padding: "100px 40px", background: "linear-gradient(155deg,#FFF7ED 0%,#ECFDF5 50%,#F5F3FF 100%)" }} className="et-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 76 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#059669", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>Our Process</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#1A1A2E", marginTop: 10, marginBottom: 16 }}>How We Build EdTech Software</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>A learner-centred development process — designed around engagement, accessibility, pedagogical best practice, and real-world scale.</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {PROCESS.map((step) => (
            <div key={step.n} style={{
              background: "#fff", borderRadius: 24, padding: "34px 30px",
              border: "1.5px solid #FED7AA",
              boxShadow: "0 4px 20px rgba(249,115,22,.06)",
              position: "relative", overflow: "hidden",
              transition: "all .32s ease",
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = step.color; e.currentTarget.style.boxShadow = `0 20px 56px ${step.color}16`; e.currentTarget.style.transform = "translateY(-4px)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#FED7AA"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(249,115,22,.06)"; e.currentTarget.style.transform = "none"; }}
            >
              <div style={{ position: "absolute", top: 20, right: 24, fontFamily: "'Sora',sans-serif", fontSize: 56, fontWeight: 900, color: `${step.color}10`, lineHeight: 1, pointerEvents: "none" }}>{step.n}</div>
              <div style={{ width: 58, height: 58, borderRadius: 16, background: `${step.color}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 22, border: `1.5px solid ${step.color}22` }}>
                {step.icon}
              </div>
              <span style={{ fontSize: 11, fontWeight: 800, color: step.color, background: `${step.color}12`, padding: "3px 12px", borderRadius: 99, display: "inline-block", marginBottom: 12, letterSpacing: ".04em", fontFamily: "'Sora',sans-serif" }}>STEP {step.n}</span>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: "#1A1A2E", marginBottom: 10 }}>{step.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>{step.desc}</p>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${step.color},${step.color}44)` }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Case Studies ──────────────────────────────────────────────────────────────
function CaseStudies() {
  const [hov, setHov] = useState(null);
  return (
    <section id="case-studies" style={{ padding: "100px 40px", background: "#fff" }} className="et-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#DC2626", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>Case Studies</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#1A1A2E", marginTop: 10, marginBottom: 16 }}>EdTech Platforms We're Proud Of</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8, fontFamily: "'Sora',sans-serif" }}>Production learning platforms serving real learners — with the completion rates and engagement metrics that matter in education.</p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 68 }}>
          {CASE_STUDIES.map((cs, idx) => (
            <div key={cs.title} style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: 56, alignItems: "center",
              direction: idx % 2 === 0 ? "ltr" : "rtl",
            }} className="et-cs-grid">
              <div style={{ direction: "ltr", position: "relative", borderRadius: 24, overflow: "hidden", boxShadow: `0 32px 80px ${cs.color}1E` }}
                onMouseEnter={() => setHov(cs.title)}
                onMouseLeave={() => setHov(null)}
              >
                <img src={cs.img} alt={cs.title} style={{ width: "100%", height: 380, objectFit: "cover", display: "block", transition: "transform .5s ease", transform: hov === cs.title ? "scale(1.05)" : "scale(1)" }} />
                <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top,${cs.color}44,transparent 60%)` }} />
                <div style={{ position: "absolute", top: 20, left: 20, background: cs.color, color: "#fff", fontSize: 12, fontWeight: 700, padding: "5px 16px", borderRadius: 99, fontFamily: "'Sora',sans-serif" }}>{cs.industry}</div>
              </div>

              <div style={{ direction: "ltr" }}>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px,3vw,40px)", fontWeight: 900, color: "#1A1A2E", marginBottom: 14 }}>{cs.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.85, marginBottom: 26, fontFamily: "'Sora',sans-serif" }}>{cs.desc}</p>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 30 }}>
                  {cs.tags.map(t => (
                    <span key={t} style={{ background: `${cs.color}0E`, color: cs.color, fontSize: 12, fontWeight: 700, padding: "5px 13px", borderRadius: 99, border: `1px solid ${cs.color}22`, fontFamily: "'Sora',sans-serif" }}>{t}</span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 32, marginBottom: 36, flexWrap: "wrap" }}>
                  {cs.metrics.map(m => (
                    <div key={m.lbl}>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 28, color: cs.color, lineHeight: 1 }}>{m.val}</div>
                      <div style={{ fontSize: 12, color: "#6B7280", fontWeight: 600, marginTop: 5, fontFamily: "'Sora',sans-serif" }}>{m.lbl}</div>
                    </div>
                  ))}
                </div>
                <a href="#" style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: cs.color, color: "#fff",
                  padding: "13px 28px", borderRadius: 12, fontWeight: 700, fontSize: 14,
                  textDecoration: "none", boxShadow: `0 6px 20px ${cs.color}30`,
                  transition: "transform .22s, box-shadow .22s", fontFamily: "'Sora',sans-serif",
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 10px 28px ${cs.color}44`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = `0 6px 20px ${cs.color}30`; }}
                >Read Full Case Study →</a>
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: "center", marginTop: 68 }}>
          <Link to="/portfolio" style={{
            background: "#fff", color: "#F97316", border: "2px solid #FED7AA",
            padding: "14px 36px", borderRadius: 12, fontWeight: 700, fontSize: 15,
            textDecoration: "none", fontFamily: "'Sora',sans-serif", transition: "all .22s", display: "inline-block",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = "#FFF7ED"; e.currentTarget.style.borderColor = "#FB923C"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#FED7AA"; }}
          >View All EdTech Projects →</Link>
        </div>
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <section style={{ padding: "100px 40px", background: "#FFFBF5" }} className="et-section">
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#8B5CF6", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Sora',sans-serif" }}>FAQ</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,50px)", fontWeight: 900, color: "#1A1A2E", marginTop: 10, marginBottom: 16 }}>Frequently Asked Questions</h2>
          <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.7, fontFamily: "'Sora',sans-serif" }}>Everything you need to know before starting your EdTech technology project.</p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {FAQS.map((faq, i) => (
            <div key={i} style={{
              background: "#fff", borderRadius: 18,
              border: `2px solid ${open === i ? "#F97316" : "#FED7AA"}`,
              overflow: "hidden", transition: "border-color .25s, box-shadow .25s",
              boxShadow: open === i ? "0 8px 36px rgba(249,115,22,.1)" : "0 1px 4px rgba(0,0,0,.04)",
            }}>
              <button onClick={() => setOpen(open === i ? null : i)} style={{
                width: "100%", padding: "22px 30px", background: "none", border: "none",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                cursor: "pointer", fontFamily: "'Sora',sans-serif",
              }}>
                <span style={{ fontWeight: 600, fontSize: 15.5, color: "#1A1A2E", textAlign: "left", lineHeight: 1.45, flex: 1, marginRight: 18 }}>{faq.q}</span>
                <div style={{
                  width: 34, height: 34, borderRadius: "50%",
                  background: open === i ? "linear-gradient(135deg,#C2410C,#F97316)" : "#FFF7ED",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .28s",
                }}>
                  <span style={{ color: open === i ? "#fff" : "#F97316", fontSize: 19, lineHeight: 1, display: "block", transform: open === i ? "rotate(45deg)" : "none", transition: "transform .28s" }}>+</span>
                </div>
              </button>
              {open === i && (
                <div style={{ padding: "0 30px 26px", animation: "fadeIn .22s ease" }}>
                  <p style={{ color: "#6B7280", fontSize: 14.5, lineHeight: 1.85, borderTop: "1px solid #FFF7ED", paddingTop: 18, fontFamily: "'Sora',sans-serif" }}>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function EdtechSolution() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { font-family: 'Sora', -apple-system, sans-serif; background: #fff; color: #0F172A; overflow-x: hidden; }

        @keyframes fadeIn  { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:none; } }
        @keyframes float   { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-10px); } }
        @keyframes pulse   { 0%,100% { opacity:1; } 50% { opacity:.4; } }
        @keyframes etPulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:.45; transform:scale(0.96); } }

        /* ── Desktop base ── */
        .nav-links       { display:flex; align-items:center; gap:4px; }
        .nav-cta         { display:flex !important; }
        .mob-menu-btn    { display:none; }

        .et-hero-grid    { display:grid; grid-template-columns:1fr 1fr; gap:64px; align-items:center; }
        .et-stats-grid   { display:grid; grid-template-columns:repeat(4,1fr); gap:32px; text-align:center; }
        .et-cs-grid      { display:grid; grid-template-columns:1fr 1fr; gap:52px; align-items:center; }
        .footer-grid     { display:grid; grid-template-columns:2fr 1fr 1fr 1fr; gap:48px; }

        /* ── ≤1100px ── */
        @media (max-width:1100px) {
          .et-hero-grid  { grid-template-columns:1fr 1fr; gap:40px; }
          .footer-grid   { grid-template-columns:1fr 1fr; gap:32px; }
          .et-stats-grid { grid-template-columns:repeat(2,1fr); }
        }

        /* ── ≤900px ── */
        @media (max-width:900px) {
          .et-hero-grid    { grid-template-columns:1fr; gap:48px; }
          .et-cs-grid      { grid-template-columns:1fr !important; gap:36px !important; direction:ltr !important; }
          .footer-grid     { grid-template-columns:1fr 1fr; gap:32px; }
          .nav-links       { display:none !important; }
          .nav-cta         { display:none !important; }
          .mob-menu-btn    { display:flex !important; }
          .et-section      { padding-left:20px !important; padding-right:20px !important; }
        }

        /* ── ≤640px ── */
        @media (max-width:640px) {
          .footer-grid     { grid-template-columns:1fr; }
          .et-stats-grid   { grid-template-columns:repeat(2,1fr); gap:24px; }
          .et-section      { padding-left:16px !important; padding-right:16px !important; padding-top:64px !important; padding-bottom:64px !important; }
          section          { padding-left:16px !important; padding-right:16px !important; }
          footer           { padding-left:16px !important; padding-right:16px !important; }
        }

        /* ── ≤480px ── */
        @media (max-width:480px) {
          .footer-grid     { grid-template-columns:1fr !important; gap:28px; }
          .et-stats-grid   { grid-template-columns:1fr 1fr; gap:20px; }
        }

        /* ── ≤500px ── */
        @media (max-width:500px) {
          .topbar-right    { display:none !important; }
        }

        /* ── Navbar padding on mobile ── */
        @media (max-width:640px) {
          nav > div:nth-child(2) { padding: 0 16px !important; }
          nav > div:first-child  { padding: 6px 16px !important; }
        }

        /* ── Hero floating badges — prevent overflow on mobile ── */
        @media (max-width:640px) {
          .et-hero-grid > div:last-child { margin-top: 8px; }
        }
      `}</style>

      <Navbar />
      <Hero />
      <StatsBar />
      <ServicesGrid />
      <WhyUs />
      <TechStack />
      <ProcessSection />
      <CaseStudies />
      <FAQ />
    </>
  );
}